import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSession } from './SessionContext';
import { useTapTracker } from '../hooks/useTapTracker';
import { useOrbSound } from '../hooks/useOrbSound';
import { askOrb, reportShareEvent } from '../services/orb';

/**
 * State for one round of Ask The Orb, shared by every game page.
 *
 * The question and the revealed prophecy are kept in sessionStorage so a
 * refresh on /charge, /prophecy or /share lands back on the same round.
 * Tap signals are per-charge and deliberately not persisted.
 */

const GAME_KEY = 'orbGame';

function readStoredGame() {
  try {
    const game = JSON.parse(sessionStorage.getItem(GAME_KEY) || '{}');
    return { question: game.question || '', prophecy: game.prophecy || null };
  } catch {
    return { question: '', prophecy: null };
  }
}

const GameContext = createContext(null);

export function GameProvider({ children }) {
  const { token, userId, name, email } = useSession();
  const tapTracker = useTapTracker();
  const sound = useOrbSound();
  const { getStats, reset: resetTaps } = tapTracker;
  const { playReveal } = sound;

  const [question, setQuestion] = useState(() => readStoredGame().question);
  const [prophecy, setProphecy] = useState(() => readStoredGame().prophecy);
  const [loading, setLoading] = useState(false);
  const [askError, setAskError] = useState(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(GAME_KEY, JSON.stringify({ question, prophecy }));
    } catch {
      /* sessionStorage unavailable — the round just won't survive a refresh */
    }
  }, [question, prophecy]);

  const consultOrb = useCallback(async () => {
    setLoading(true);
    setAskError(null);
    try {
      const stats = getStats();
      const result = await askOrb({ token, question: question.trim(), userId, name, email, ...stats });
      setProphecy(result);
      playReveal();
    } catch (err) {
      setAskError(err?.response?.data?.message || err.message || 'The orb could not be reached. Try again.');
    } finally {
      setLoading(false);
    }
  }, [token, question, userId, name, email, getStats, playReveal]);

  /** Clears the orb for another charge of the same question. */
  const resetCharge = useCallback(() => {
    resetTaps();
    setProphecy(null);
    setAskError(null);
  }, [resetTaps]);

  /** Clears everything for a brand new question. */
  const resetRound = useCallback(() => {
    resetCharge();
    setQuestion('');
  }, [resetCharge]);

  const reportShare = useCallback(
    (action, format) => reportShareEvent({ token, rowId: prophecy?.rowId, action, format }),
    [token, prophecy]
  );

  // The tap/sound hooks hand back fresh objects each render, so memoising here would never hit.
  const value = {
    question,
    setQuestion,
    prophecy,
    loading,
    askError,
    consultOrb,
    resetCharge,
    resetRound,
    reportShare,
    ...tapTracker,
    ...sound,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  return useContext(GameContext);
}
